import { WISHLIST_SOCIAL_SHARE_CHANNEL_NAMES } from "@apps/wishlist/constants/constants";
import { JsonMimeType } from "@lib/ApiClient";
import AuthApiClient from "@lib/ApiClient/AuthApiClient";
import { Observable, map } from "rxjs";
import { create } from "superstruct";
import { inject, singleton } from "tsyringe";

import { BaseGiftRegistrantEntity } from "../entities/BaseGiftRegistrantEntity";
import { BaseGiftRegistryEntity } from "../entities/BaseGiftRegistryEntity";
import { CustomerAddressesResponseEntity } from "../entities/CustomerAddressesResponseEntity";
import { GiftRegistrantEntity } from "../entities/GiftRegistrantEntity";
import { GiftRegistriesWithItemsResponseEntity } from "../entities/GiftRegistriesWithItemsResponseEntity";
import { GiftRegistryEntity } from "../entities/GiftRegistryEntity";
import { GiftRegistryItemsResponseEntity } from "../entities/GiftRegistryItemsResponseEntity";
import { GiftRegistryTypesResponseEntity } from "../entities/GiftRegistryTypesResponseEntity";
import { ProductStatusInRegistryResponseEntity } from "../entities/ProductStatusInRegistryResponseEntity";
import { SharedRegistryResponseEntity } from "../entities/SharedRegistryResponseEntity";
import ChangeGiftRegistryItemResponseSchema from "../schemas/ChangeGiftRegistryItemResponseSchema";
import CreateDraftOrderResponseSchema from "../schemas/CreateDraftOrderResponseSchema";
import CreateGiftRegistryResponseSchema from "../schemas/CreateGiftRegistryResponseSchema";
import CustomerAddressesResponseSchema from "../schemas/CustomerAddressesResponseSchema";
import DeleteGiftRegistryResponseSchema from "../schemas/DeleteGiftRegistryResponseSchema";
import GiftRegistriesWithItemsResponseSchema from "../schemas/GiftRegistriesWithItemsResponseSchema";
import GiftRegistryItemsResponseSchema from "../schemas/GiftRegistryItemsResponseSchema";
import GiftRegistryTypesResponseSchema from "../schemas/GiftRegistryTypesResponseSchema";
import ProductStatusInRegistryResponseSchema from "../schemas/ProductStatusInRegistryResponseSchema";
import SharedRegistryResponseSchema from "../schemas/SharedRegistryResponseSchema";
import ShareGiftRegistryResponseSchema from "../schemas/ShareGiftRegistryResponseSchema";
import UpdateGiftRegistryResponseSchema from "../schemas/UpdateGiftRegistryResponseSchema";
import { giftRegistryAuthApiClientToken } from "../tokens";

export type GetGiftRegistryItemsParams = {
    giftRegistryId: number;
    offset?: number;
    perPage?: number;
};

export type GetProductStatusInGiftRegistryParams = {
    productId: number;
};

export type GetSharedGiftRegistryParams = {
    shareToken: string;
};

export type CreateGiftRegistryRequestBody = {
    giftRegistry: BaseGiftRegistryEntity;
    giftRegistrant: BaseGiftRegistrantEntity;
};

export type UpdateGiftRegistryRequestBody = {
    giftRegistry: GiftRegistryEntity;
    giftRegistrant: GiftRegistrantEntity;
};

export type DeleteGiftRegistryRequestBody = {
    giftRegistryId: number;
};

export type CreateGiftRegistryItemRequestBody = {
    giftRegistryId: number;
    productId: number;
    variantId: number;
    quantity: number;
    tag: string | null;
};

export type DeleteGiftRegistryItemRequestBody = {
    giftRegistryItemId: number;
};

export type MoveGiftRegistryItemRequestBody = {
    giftRegistryItemId: number;
    fromGiftRegistryId: number;
    toGiftRegistryId: number;
};

export type UpdateGiftRegistryItemRequestBody = {
    giftRegistryItem: {
        giftRegistryId: number;
        productId: number;
        variantId: number;
        quantity: number;
        tag: string | null;
        id: number;
    };
};

export type CreateDraftOrderRequestBody = {
    giftRegistryId: number;
    products: {
        productId: number;
        variantId: number;
        quantity: number;
    }[];
};

@singleton()
export class GiftRegistryApiService {
    constructor(
        @inject(giftRegistryAuthApiClientToken)
        private readonly apiClient: AuthApiClient
    ) {}

    getGiftRegistriesWithItems(): Observable<GiftRegistriesWithItemsResponseEntity> {
        return this.apiClient
            .get({
                url: "/getGiftRegistriesWithItems",
                responseType: "json",
            })
            .pipe(
                map((response) => {
                    return create(
                        response.body,
                        GiftRegistriesWithItemsResponseSchema
                    );
                })
            );
    }

    getGiftRegistryItems(
        queryParams: GetGiftRegistryItemsParams
    ): Observable<GiftRegistryItemsResponseEntity> {
        return this.apiClient
            .get({
                url: "/getGiftRegistryItems",
                responseType: "json",
                queryParams,
            })
            .pipe(
                map((response) => {
                    return create(
                        response.body,
                        GiftRegistryItemsResponseSchema
                    );
                })
            );
    }

    getGiftRegistryTypes(): Observable<GiftRegistryTypesResponseEntity> {
        return this.apiClient
            .get({
                url: "/getGiftRegistryTypes",
                responseType: "json",
            })
            .pipe(
                map((response) => {
                    return create(
                        response.body,
                        GiftRegistryTypesResponseSchema
                    );
                })
            );
    }

    getCustomerAddresses(): Observable<CustomerAddressesResponseEntity> {
        return this.apiClient
            .get({
                url: "/getCustomerAddresses",
                responseType: "json",
            })
            .pipe(
                map((response) => {
                    return create(
                        response.body,
                        CustomerAddressesResponseSchema
                    );
                })
            );
    }

    getProductStatusInGiftRegistry(
        queryParams: GetProductStatusInGiftRegistryParams
    ): Observable<ProductStatusInRegistryResponseEntity> {
        return this.apiClient
            .get({
                url: "/getProductStatusInGiftRegistry",
                responseType: "json",
                queryParams,
            })
            .pipe(
                map((response) => {
                    return create(
                        response.body,
                        ProductStatusInRegistryResponseSchema
                    );
                })
            );
    }

    getSharedGiftRegistry(
        queryParams: GetSharedGiftRegistryParams
    ): Observable<SharedRegistryResponseEntity> {
        return this.apiClient
            .get({
                url: "/getSharedGiftRegistry",
                responseType: "json",
                queryParams,
            })
            .pipe(
                map((response) => {
                    return create(response.body, SharedRegistryResponseSchema);
                })
            );
    }

    createGiftRegistry(data: CreateGiftRegistryRequestBody) {
        return this.apiClient
            .post({
                url: "/createGiftRegistry",
                body: JSON.stringify(data),
                contentType: JsonMimeType,
            })
            .pipe(
                map((response) => {
                    const result = create(
                        response.body,
                        CreateGiftRegistryResponseSchema
                    );

                    return result.giftRegistryWithRegistrant;
                })
            );
    }

    updateGiftRegistry(data: UpdateGiftRegistryRequestBody) {
        return this.apiClient
            .post({
                url: "/updateGiftRegistry",
                body: JSON.stringify(data),
                contentType: JsonMimeType,
            })
            .pipe(
                map((response) => {
                    const result = create(
                        response.body,
                        UpdateGiftRegistryResponseSchema
                    );

                    return result.giftRegistryWithRegistrant;
                })
            );
    }

    deleteGiftRegistry(data: DeleteGiftRegistryRequestBody) {
        return this.apiClient
            .post({
                url: "/deleteGiftRegistry",
                body: JSON.stringify(data),
                contentType: JsonMimeType,
            })
            .pipe(
                map((response) => {
                    const result = create(
                        response.body,
                        DeleteGiftRegistryResponseSchema
                    );

                    return result.deletedGiftRegistry;
                })
            );
    }

    shareGiftRegistry(
        channelName: WISHLIST_SOCIAL_SHARE_CHANNEL_NAMES,
        giftRegistryId: number
    ) {
        let url = "/shareGiftRegistry";
        if (channelName !== WISHLIST_SOCIAL_SHARE_CHANNEL_NAMES.copyShareLink) {
            url += `/${channelName}`;
        }

        return this.apiClient
            .post({
                url,
                body: JSON.stringify({ giftRegistryId }),
                contentType: JsonMimeType,
            })
            .pipe(
                map((response) => {
                    const result = create(
                        response.body,
                        ShareGiftRegistryResponseSchema
                    );
                    return result;
                })
            );
    }

    createGiftRegistryItem(data: CreateGiftRegistryItemRequestBody) {
        return this.apiClient
            .post({
                url: "/createGiftRegistryItem",
                body: JSON.stringify(data),
                contentType: JsonMimeType,
            })
            .pipe(
                map((response) => {
                    const result = create(
                        response.body,
                        ChangeGiftRegistryItemResponseSchema
                    );

                    return result.giftRegistryItem;
                })
            );
    }

    deleteGiftRegistryItem(data: DeleteGiftRegistryItemRequestBody) {
        return this.apiClient
            .post({
                url: "/deleteGiftRegistryItem",
                body: JSON.stringify(data),
                contentType: JsonMimeType,
            })
            .pipe(
                map((response) => {
                    const result = create(
                        response.body,
                        ChangeGiftRegistryItemResponseSchema
                    );

                    return result.giftRegistryItem;
                })
            );
    }

    moveGiftRegistryItem(data: MoveGiftRegistryItemRequestBody) {
        return this.apiClient
            .post({
                url: "/moveGiftRegistryItem",
                body: JSON.stringify(data),
                contentType: JsonMimeType,
            })
            .pipe(
                map((response) => {
                    const result = create(
                        response.body,
                        ChangeGiftRegistryItemResponseSchema
                    );

                    return result.giftRegistryItem;
                })
            );
    }

    updateGiftRegistryItem(data: UpdateGiftRegistryItemRequestBody) {
        return this.apiClient
            .post({
                url: "/updateGiftRegistryItem",
                body: JSON.stringify(data),
                contentType: JsonMimeType,
            })
            .pipe(
                map((response) => {
                    const result = create(
                        response.body,
                        ChangeGiftRegistryItemResponseSchema
                    );

                    return result.giftRegistryItem;
                })
            );
    }

    createDraftOrder(data: CreateDraftOrderRequestBody) {
        return this.apiClient
            .post({
                url: "/createDraftOrder",
                body: JSON.stringify(data),
                contentType: JsonMimeType,
            })
            .pipe(
                map((response) => {
                    return create(
                        response.body,
                        CreateDraftOrderResponseSchema
                    );
                })
            );
    }
}
